import { useState, useCallback } from 'react';
import { LoadingSpinner } from './LoadingSpinner';
import { ConfirmationModal } from './ConfirmationModal';

interface BulkOperationProgress {
  total: number;
  completed: number;
  failed: number;
  current?: string;
  errors: Array<{
    id: string;
    name: string;
    error: string;
  }>;
}

interface BulkOperationProps {
  selectedItems: any[];
  onClearSelection: () => void;
  operations: Array<{
    key: string;
    label: string;
    icon: string;
    variant: 'primary' | 'success' | 'warning' | 'danger' | 'info';
    requiresConfirmation?: boolean;
    confirmationTitle?: string;
    confirmationMessage?: string;
    onExecute: (items: any[]) => Promise<BulkOperationProgress>;
  }>;
  className?: string;
}

export function BulkOperations({
  selectedItems,
  onClearSelection,
  operations,
  className = '',
}: BulkOperationProps): JSX.Element {
  const [activeOperation, setActiveOperation] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingOperation, setPendingOperation] = useState<any>(null);
  const [progress, setProgress] = useState<BulkOperationProgress | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [operationComplete, setOperationComplete] = useState(false);

  const handleOperationClick = useCallback((operation: any) => {
    if (operation.requiresConfirmation) {
      setPendingOperation(operation);
      setShowConfirmation(true);
    } else {
      executeOperation(operation);
    }
  }, []);

  const executeOperation = useCallback(async (operation: any) => {
    try {
      setActiveOperation(operation.key);
      setShowProgressModal(true);
      setOperationComplete(false);
      setProgress({
        total: selectedItems.length,
        completed: 0,
        failed: 0,
        errors: [],
      });

      const result = await operation.onExecute(selectedItems);
      setProgress(result);
      setOperationComplete(true);
      
      // Auto-close if no errors
      if (result.failed === 0) {
        setTimeout(() => {
          setShowProgressModal(false);
          setActiveOperation(null);
          onClearSelection();
        }, 2000);
      }
    } catch (error: any) {
      setProgress({
        total: selectedItems.length,
        completed: 0,
        failed: selectedItems.length,
        errors: selectedItems.map(item => ({
          id: item.id,
          name: item.name || item.commonName || `Item ${item.id}`,
          error: error.message || 'Unknown error occurred',
        })),
      });
      setOperationComplete(true);
    }
  }, [selectedItems, onClearSelection]);

  const handleConfirmOperation = useCallback(() => {
    setShowConfirmation(false);
    if (pendingOperation) {
      executeOperation(pendingOperation);
      setPendingOperation(null);
    }
  }, [pendingOperation, executeOperation]);

  const handleCancelOperation = useCallback(() => {
    setShowConfirmation(false);
    setPendingOperation(null);
  }, []);

  const handleCloseProgress = useCallback(() => {
    setShowProgressModal(false);
    setActiveOperation(null);
    setProgress(null);
    setOperationComplete(false);
    if (progress && progress.completed > 0) {
      onClearSelection();
    }
  }, [progress, onClearSelection]);

  if (selectedItems.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={`bulk-operations ${className}`}>
        <div className="card card-outline card-info">
          <div className="card-body py-2">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center mb-2 mb-md-0">
                <span className="badge badge-info mr-2">
                  {selectedItems.length} selected
                </span>
                <span className="text-muted d-none d-md-inline">
                  Bulk operations available:
                </span>
              </div>
              
              <div className="d-flex flex-wrap align-items-center">
                <div className="btn-group-vertical btn-group-sm d-md-none w-100 mb-2">
                  {operations.map((operation) => (
                    <button
                      key={operation.key}
                      className={`btn btn-${operation.variant}`}
                      onClick={() => handleOperationClick(operation)}
                      disabled={activeOperation !== null}
                    >
                      <i className={`fas ${operation.icon} mr-2`}></i>
                      {operation.label}
                    </button>
                  ))}
                  <button
                    className="btn btn-outline-secondary"
                    onClick={onClearSelection}
                  >
                    <i className="fas fa-times mr-2"></i>
                    Clear Selection
                  </button>
                </div>
                
                <div className="d-none d-md-flex align-items-center">
                  <div className="btn-group mr-2">
                    {operations.map((operation) => (
                      <button
                        key={operation.key}
                        className={`btn btn-sm btn-${operation.variant}`}
                        onClick={() => handleOperationClick(operation)}
                        disabled={activeOperation !== null}
                        title={operation.label}
                      >
                        <i className={`fas ${operation.icon} mr-1`}></i>
                        <span className="d-none d-lg-inline">{operation.label}</span>
                      </button>
                    ))}
                  </div>
                  
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={onClearSelection}
                    title="Clear selection"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && pendingOperation && (
        <ConfirmationModal
          isOpen={showConfirmation}
          title={pendingOperation.confirmationTitle || `Confirm ${pendingOperation.label}`}
          message={
            pendingOperation.confirmationMessage || 
            `Are you sure you want to ${pendingOperation.label.toLowerCase()} ${selectedItems.length} item(s)?`
          }
          confirmText="Proceed"
          cancelText="Cancel"
          onConfirm={handleConfirmOperation}
          onCancel={handleCancelOperation}
          confirmVariant="warning"
        />
      )}

      {/* Progress Modal */}
      {showProgressModal && progress && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fas fa-tasks mr-2"></i>
                    Bulk Operation Progress
                  </h4>
                  {operationComplete && (
                    <button
                      type="button"
                      className="close"
                      onClick={handleCloseProgress}
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  )}
                </div>
                <div className="modal-body">
                  {/* Progress Overview */}
                  <div className="row mb-4">
                    <div className="col-md-4">
                      <div className="info-box bg-info">
                        <span className="info-box-icon">
                          <i className="fas fa-list"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Total</span>
                          <span className="info-box-number">{progress.total}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="info-box bg-success">
                        <span className="info-box-icon">
                          <i className="fas fa-check"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Completed</span>
                          <span className="info-box-number">{progress.completed}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="info-box bg-danger">
                        <span className="info-box-icon">
                          <i className="fas fa-times"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Failed</span>
                          <span className="info-box-number">{progress.failed}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Progress</span>
                      <span>{Math.round(((progress.completed + progress.failed) / progress.total) * 100)}%</span>
                    </div>
                    <div className="progress">
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                      ></div>
                      <div
                        className="progress-bar bg-danger"
                        style={{ width: `${(progress.failed / progress.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Current Operation */}
                  {!operationComplete && progress.current && (
                    <div className="alert alert-info">
                      <div className="d-flex align-items-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Processing: {progress.current}</span>
                      </div>
                    </div>
                  )}

                  {/* Operation Complete */}
                  {operationComplete && (
                    <div className={`alert ${progress.failed === 0 ? 'alert-success' : 'alert-warning'}`}>
                      <h5>
                        <i className={`fas ${progress.failed === 0 ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                        Operation Complete
                      </h5>
                      <p>
                        {progress.failed === 0 
                          ? `Successfully processed all ${progress.completed} items.`
                          : `Processed ${progress.completed} items successfully, ${progress.failed} failed.`
                        }
                      </p>
                    </div>
                  )}

                  {/* Error Details */}
                  {progress.errors.length > 0 && (
                    <div className="mt-3">
                      <h6 className="text-danger">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        Errors ({progress.errors.length})
                      </h6>
                      <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {progress.errors.map((error, index) => (
                              <tr key={index}>
                                <td className="font-weight-bold">{error.name}</td>
                                <td className="text-danger">{error.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  {operationComplete ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCloseProgress}
                    >
                      Close
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled
                    >
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Processing...</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}